import { UserStore } from "@faire/retailer-visitor-shared/stores/domain/UserStore";
import { getGlobalProperty } from "@faire/web--source/common/globals/getGlobalProperty";
import { filterAndProcessHotjarSettings } from "@faire/web--source/common/hotjar/hotjarUtils";
import { getLocaleKey } from "@faire/web--source/common/serialized-data/getLocaleKey";
import { isProduction } from "@faire/web--source/common/user-agent/isProduction";
import { uuid } from "@faire/web--source/common/uuid";
// Import with absolute path
import { UXCam } from "@faire/web--source/common/uxcam/uxcam";
import { IUser } from "@faire/web-api--source/indigofair/data/IUser";

import { getHotjarSettingsValues } from "../serialized-data/getHotjarSettingsValues";
import { getSettingRecordSessionUXCam } from "../settings/getSettingRecordSessionUXCam";

// UXCam app key from the provided snippet
const UXCAM_APP_KEY = "v5jht5ruigcmi75";
const UXCAM_CONFIG_MAX_SETTINGS = 200;

/**
 * Initializes UXCam session recording for non-admin users
 * when the recording setting is enabled
 */
export const initializeUXCam = async (): Promise<void> => {
  const { user = IUser.build({}), isAdminUser } = UserStore.get();

  const isProductionEnv = isProduction();
  const isRecordingEnabled = getSettingRecordSessionUXCam();

  if (isRecordingEnabled && (!isProductionEnv || !isAdminUser)) {
    try {
      // Initialize UXCam with app key
      UXCam.initialize(UXCAM_APP_KEY);

      const { impersonating, token, type, retailer_token } = user;
      const language = getLocaleKey()?.language;
      const country = getLocaleKey()?.country;
      const storeType = getGlobalProperty("storeType");

      const settingsValues = getHotjarSettingsValues();

      const processedSettings = filterAndProcessHotjarSettings(
        settingsValues,
        UXCAM_CONFIG_MAX_SETTINGS
      );

      // Create user properties object with base properties and experiment settings
      const userProperties = {
        impersonating,
        type,
        retailer_token,
        localeLanguage: language,
        localeCountry: country,
        storeType,
      };

      // Set user identity for UXCam
      const identifyToken = token ?? `logged-out-${uuid()}`;
      UXCam.setUserIdentity(identifyToken);

      // Set user properties
      UXCam.setUserProperties(userProperties);

      // Track experiment events for each experiment
      Object.keys(processedSettings).forEach((experimentKey) => {
        const experimentValue = processedSettings[experimentKey];
        // Track individual experiment assignment
        UXCam.event(experimentKey, {
          value: experimentValue,
        });
      });
    } catch (error) {
      return;
    }
  }
};
